import React, { useMemo } from 'react';

import { useParams } from 'react-router-dom';

import { Button, FontAwesomeIcon } from '@appcharge/shared-ui';
import { useProduct } from 'api/useProduct';
import { EProductPriority } from 'constants/enums';

import Input from 'components/Input/Input';
import InputSelectDropdown, {
  type InputSelectDropdownOption
} from 'components/InputSelectDropdown/InputSelectDropdown';
import { Product } from 'components/ProductsForm/ProductsForm.types';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from 'components/ui/table';

import {
  type IProductsSequenceTableProduct,
  type IProductsSequenceTableProps
} from './ProductsSequenceTable.types';
import {
  DEFAULT_MAX_PRODUCTS,
  resolveProductId
} from './ProductsSequenceTable.utils';

const ProductsSequenceTable: React.FC<IProductsSequenceTableProps> = ({
  products,
  sequenceIndex,
  onProductChange,
  onPriorityChange,
  onQuantityChange,
  onRemoveProduct,
  onAddProduct,
  localQuantities,
  priorityOptions = [],
  testIds,
  priorityColumnLabel = 'Priority',
  maxProducts = DEFAULT_MAX_PRODUCTS,
  addButtonLabel = 'Add New Product',
  hideSelectedProducts = false,
  showPriorityColumn = true,
  showAddButton = true,
  showRemoveButton = true
}) => {
  const { publisherId } = useParams();
  const { data: productsData } = useProduct(publisherId).getProducts;

  const productOptions: InputSelectDropdownOption[] = useMemo(() => {
    if (!productsData?.result) return [];
    return productsData.result.map((product: Product) => ({
      value: String(product.productId),
      label: product.name,
      image: product.images?.[0]?.url
    }));
  }, [productsData?.result]);

  const selectedProductIds = useMemo(
    () => new Set(products.map(resolveProductId).filter(Boolean)),
    [products]
  );

  const getOptionsForRow = (
    productIndex: number
  ): InputSelectDropdownOption[] => {
    if (!hideSelectedProducts) return productOptions;
    const currentProductId = resolveProductId(products[productIndex]);
    return productOptions.filter(
      (option) =>
        String(option.value) === currentProductId ||
        !selectedProductIds.has(String(option.value))
    );
  };

  const getSelectedProductOption = (
    product: IProductsSequenceTableProduct
  ): InputSelectDropdownOption | undefined => {
    const productId = resolveProductId(product);
    if (!productId) return undefined;
    return productOptions.find((option) => String(option.value) === productId);
  };

  const getSelectedPriorityOption = (
    product: IProductsSequenceTableProduct
  ): InputSelectDropdownOption | undefined => {
    if (product?.priority === undefined) return undefined;
    return priorityOptions.find((option) => option.value === product.priority);
  };

  const canAddMore = products.length < maxProducts;

  return (
    <div className="flex flex-col gap-4">
      <div className="border border-base-border rounded-lg overflow-hidden">
        <Table disableScrollWrapper>
          <TableHeader>
            <TableRow className="bg-transparent hover:bg-transparent border-b border-base-border">
              <TableHead className="text-sm font-medium text-base-foreground h-12 px-4 border-r border-base-border w-2/5">
                Product
              </TableHead>
              {showPriorityColumn && (
                <TableHead className="text-sm font-medium text-base-foreground h-12 px-4 border-r border-base-border w-1/4">
                  {priorityColumnLabel}
                </TableHead>
              )}
              <TableHead className="text-sm font-medium text-base-foreground h-12 px-4">
                Amount
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map((product, productIndex) => {
              const rowNumber = productIndex + 1;
              const localQuantity = localQuantities[productIndex];
              let quantityValue: number | string = product?.quantity || '';
              if (localQuantity !== undefined && localQuantity !== null) {
                quantityValue = localQuantity;
              }
              return (
                <TableRow
                  key={productIndex}
                  className="hover:bg-transparent border-b border-base-border last:border-b-0"
                  data-testid={`${testIds.productRowPrefix}-${rowNumber}`}
                >
                  <TableCell className="px-4 py-3 border-r border-base-border w-2/5 max-w-0">
                    <div className="w-full overflow-hidden">
                      <InputSelectDropdown
                        options={getOptionsForRow(productIndex)}
                        onOptionChange={(value) =>
                          onProductChange(productIndex, value || null)
                        }
                        defaultSelected={getSelectedProductOption(product)}
                        placeholder="Select Product"
                        buttonClassName="h-10"
                        buttonDataTestId={`${testIds.productButtonPrefix}${rowNumber}`}
                        listDataTestId={testIds.productListId}
                      />
                    </div>
                  </TableCell>
                  {showPriorityColumn && (
                    <TableCell className="px-4 py-3 border-r border-base-border w-1/4 max-w-0">
                      <div className="w-full overflow-hidden">
                        <InputSelectDropdown
                          options={priorityOptions}
                          onOptionChange={(value) =>
                            onPriorityChange?.(
                              productIndex,
                              value as EProductPriority
                            )
                          }
                          defaultSelected={getSelectedPriorityOption(product)}
                          placeholder="Select"
                          buttonClassName="h-10"
                          buttonDataTestId={`${testIds.priorityButtonPrefix}${rowNumber}`}
                          listDataTestId={testIds.priorityListId}
                        />
                      </div>
                    </TableCell>
                  )}
                  <TableCell className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Input
                        name={`amount-${sequenceIndex}-${productIndex}`}
                        value={quantityValue}
                        onChange={(e) =>
                          onQuantityChange(
                            e as React.ChangeEvent<HTMLInputElement>,
                            productIndex
                          )
                        }
                        type="number"
                        placeholder="0"
                        dataTestId={
                          testIds.quantityInputPrefix
                            ? `${testIds.quantityInputPrefix}-${rowNumber}`
                            : undefined
                        }
                      />
                      {showRemoveButton && (
                        <div
                          className={`flex-shrink-0 flex items-center justify-center w-10 h-10 cursor-pointer text-base-foreground transition-colors ${products.length <= 1 ? 'opacity-30 pointer-events-none' : ''}`}
                          data-testid={
                            testIds.deleteButtonPrefix
                              ? `${testIds.deleteButtonPrefix}-${rowNumber}`
                              : undefined
                          }
                        >
                          <FontAwesomeIcon
                            icon="fa-regular fa-trash-can"
                            className="text-base"
                            onClick={() =>
                              products.length > 1 &&
                              onRemoveProduct(productIndex)
                            }
                          />
                        </div>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {showAddButton && (
        <Button
          type="button"
          variant="outline"
          onClick={onAddProduct}
          disabled={!canAddMore}
          className="w-full bg-base-muted border-none hover:bg-violet-100 shadow-none h-10 mt-1 mb-1"
          data-testid={testIds.addButtonId}
        >
          <FontAwesomeIcon
            icon="fa-regular fa-circle-plus"
            className="mr-2 text-base"
          />
          {addButtonLabel}
        </Button>
      )}
    </div>
  );
};

export { ProductsSequenceTable };
